import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { config } from '@/config';
import { ProjectBadge } from '@/features/projects/components/ProjectBadge';
import { ProjectBadgeField } from '@/features/projects/components/ProjectBadgeField';
import {
  PipelineWebhookKeySection,
  PipelineWebhookUrlField,
} from '@/features/projects/components/ProjectWebhookFields';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Box, formatDate, SettingsSectionPanel, Stack, StatusBanner, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsValueTextSx,
  settingsWideFieldMeasureSx,
} from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import { parseWebhookTagValues } from './projectWebhookHelpers';
import type {
  useRotateProjectApiKey,
  useTestProjectPipelineWebhook,
  useUpdateProject,
} from '@/features/projects';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

export type ProjectDetailsSectionProps = {
  project: ProjectDto | null;
  nameValue: string;
  setNameValue: (value: string) => void;
  nameDirty: boolean;
  setNameDirty: (value: boolean) => void;
  badgeEnabledValue: boolean;
  setBadgeEnabledValue: (value: boolean) => void;
  badgeCharsValue: string;
  setBadgeCharsValue: (value: string) => void;
  badgeColorValue: ProjectDto['badgeColor'];
  setBadgeColorValue: (value: ProjectDto['badgeColor']) => void;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
};

export type ProjectApiKeySectionProps = {
  project: ProjectDto | null;
  apiKeyValue: string;
  setApiKeyValue: (value: string) => void;
  revokeOpen: boolean;
  setRevokeOpen: (open: boolean) => void;
  updateProject: ReturnType<typeof useUpdateProject>;
  rotateProjectApiKey: ReturnType<typeof useRotateProjectApiKey>;
  testProjectPipelineWebhook: ReturnType<typeof useTestProjectPipelineWebhook>;
  apiKeyError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  container?: 'panel' | 'none';
};

const renderPanel = (
  title: string,
  children: ReactElement,
  actions?: ReactElement,
  titleSlot?: ReactElement,
): ReactElement => (
  <SettingsSectionPanel title={title} actions={actions} titleSlot={titleSlot}>
    {children}
  </SettingsSectionPanel>
);

export const ProjectDetailsSection = ({
  project,
  nameValue,
  setNameValue,
  nameDirty,
  setNameDirty,
  badgeEnabledValue,
  setBadgeEnabledValue,
  badgeCharsValue,
  setBadgeCharsValue,
  badgeColorValue,
  setBadgeColorValue,
  updateProject,
  mutationError,
  notify,
}: ProjectDetailsSectionProps): ReactElement => {
  const projectDetailsFieldWidth = '320px';
  const previewBadgeChars = badgeCharsValue.trim().length > 0 ? badgeCharsValue : 'P';

  const syncDetailsDirty = (
    nextName: string,
    nextBadgeEnabled: boolean,
    nextBadgeChars: string,
    nextBadgeColor: ProjectDto['badgeColor'],
  ): void => {
    if (!project) return;
    setNameDirty(
      nextName.trim() !== project.name.trim() ||
        nextBadgeEnabled !== project.badgeEnabled ||
        nextBadgeChars !== project.badgeChars ||
        nextBadgeColor !== project.badgeColor,
    );
  };

  return renderPanel(
    'Project Details',
    <Stack gap="md">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${projectDetailsFieldWidth} auto`,
          alignItems: 'flex-end',
          columnGap: s('lg'),
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ width: projectDetailsFieldWidth, minWidth: 0 }}>
          <TextField
            label="Name"
            fieldStyle="settings"
            inputHeight={32}
            value={nameValue}
            onChange={(event) => {
              const nextValue = event.target.value;
              setNameValue(nextValue);
              syncDetailsDirty(nextValue, badgeEnabledValue, badgeCharsValue, badgeColorValue);
            }}
            disabled={updateProject.isPending}
          />
        </Box>
        <Box sx={{ mb: '4px', flex: '0 0 auto' }}>
          <ProjectBadge
            chars={previewBadgeChars}
            color={badgeColorValue}
            disabled={!badgeEnabledValue}
          />
        </Box>
      </Box>
      <Box sx={{ mt: s('sm') }}>
        <ProjectBadgeField
          enabled={badgeEnabledValue}
          onEnabledChange={(nextValue) => {
            setBadgeEnabledValue(nextValue);
            syncDetailsDirty(nameValue, nextValue, badgeCharsValue, badgeColorValue);
          }}
          chars={badgeCharsValue}
          color={badgeColorValue}
          disabled={updateProject.isPending}
          onCharsChange={(nextValue) => {
            setBadgeCharsValue(nextValue);
            syncDetailsDirty(nameValue, badgeEnabledValue, nextValue, badgeColorValue);
          }}
          onColorChange={(nextValue) => {
            setBadgeColorValue(nextValue);
            syncDetailsDirty(nameValue, badgeEnabledValue, badgeCharsValue, nextValue);
          }}
          textFieldColumnWidth={projectDetailsFieldWidth}
        />
      </Box>
    </Stack>,
    <>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disabled={!nameDirty || updateProject.isPending}
        onClick={() => {
          if (!project) return;
          setNameValue(project.name);
          setBadgeEnabledValue(project.badgeEnabled);
          setBadgeCharsValue(project.badgeChars);
          setBadgeColorValue(project.badgeColor);
          setNameDirty(false);
          mutationError.clearError();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        fullWidthOnMobile={false}
        disabled={
          !nameDirty ||
          updateProject.isPending ||
          (badgeEnabledValue && badgeCharsValue.length === 0)
        }
        onClick={() => {
          if (!project) return;
          updateProject.mutate(
            {
              id: project.id,
              name: nameValue,
              badgeEnabled: badgeEnabledValue,
              badgeChars: badgeCharsValue,
              badgeColor: badgeColorValue,
            },
            {
              onSuccess: (updated) => {
                setNameValue(updated.name);
                setBadgeEnabledValue(updated.badgeEnabled);
                setBadgeCharsValue(updated.badgeChars);
                setBadgeColorValue(updated.badgeColor);
                setNameDirty(false);
                mutationError.clearError();
                notify({ message: `Updated project ${updated.name}`, tone: 'green' });
              },
              onError: (err) => {
                mutationError.setError(err);
              },
            },
          );
        }}
      >
        Update
      </Button>
    </>,
  );
};

export const ProjectApiKeySection = ({
  project,
  apiKeyValue,
  setApiKeyValue,
  revokeOpen,
  setRevokeOpen,
  updateProject,
  rotateProjectApiKey,
  testProjectPipelineWebhook,
  apiKeyError,
  notify,
  container = 'panel',
}: ProjectApiKeySectionProps): ReactElement => {
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [generatedApiKeyCreatedAt, setGeneratedApiKeyCreatedAt] = useState<Date | null>(null);
  const [generatedApiKeyLastUsedAt, setGeneratedApiKeyLastUsedAt] = useState<Date | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [webhookTagsInput, setWebhookTagsInput] = useState('');
  const actionIconSize = componentSizes.auth.inputHeight - 2;
  const hasPlaintextApiKey = apiKeyValue.trim().length > 0;
  const hasConfiguredApiKey = hasPlaintextApiKey || Boolean(project?.hasProjectApiKey);
  const createdAt = generatedApiKeyCreatedAt ?? project?.projectApiKeyCreatedAt ?? null;
  const lastUsedAt = generatedApiKeyLastUsedAt ?? project?.projectApiKeyLastUsedAt ?? null;
  const createdLabel = formatDate(createdAt, { includeTime: true }) || '-';
  const lastUsedLabel = formatDate(lastUsedAt, { includeTime: true }) || '-';
  const displayApiKeyValue = hasPlaintextApiKey
    ? apiKeyValue
    : hasConfiguredApiKey
      ? '••••••••••••••••'
      : '';
  const apiBaseUrl = config.apiBaseUrl.replace(/\/+$/, '');
  const webhookEndpoint = project
    ? `${apiBaseUrl}/webhooks/pipelines/${project.id}`
    : `${apiBaseUrl}/webhooks/pipelines/{projectId}`;
  const webhookTagValues = useMemo(
    () => parseWebhookTagValues(webhookTagsInput),
    [webhookTagsInput],
  );
  const actionGroupSx = {
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    '& .MuiButton-root': {
      color: 'text.primary',
      borderColor: 'divider',
      minWidth: 0,
    },
    '& .MuiButtonGroup-grouped': {
      width: actionIconSize,
      minWidth: actionIconSize,
      maxWidth: actionIconSize,
      flex: `0 0 ${actionIconSize}px`,
      padding: 0,
    },
    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
      borderRight: '1px solid',
      borderColor: 'divider',
    },
    '& .MuiButton-root.Mui-disabled': {
      color: 'text.disabled',
    },
  } as const;
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 17,
      display: 'block',
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  } as const;
  const onCopyWebhookKey = (): void => {
    if (!hasPlaintextApiKey) return;
    void navigator.clipboard.writeText(apiKeyValue);
    notify({ message: 'API key copied to clipboard', tone: 'green' });
  };
  const onCopyWebhookUrl = (): void => {
    void navigator.clipboard.writeText(webhookEndpoint);
    notify({ message: 'Webhook URL copied to clipboard', tone: 'green' });
  };
  useEffect(() => {
    if (!project) return;
    setWebhookTagsInput((project.projectApiKeyTags ?? []).join(', '));
  }, [project?.id, project?.projectApiKeyTags]);
  const applyGeneratedWebhookKey = (): void => {
    if (!project) return;
    rotateProjectApiKey.mutate(
      { id: project.id, tags: webhookTagValues },
      {
        onSuccess: (result) => {
          setApiKeyValue(result.apiKey);
          setBannerDismissed(false);
          setGeneratedApiKeyCreatedAt(new Date(result.createdAt));
          setGeneratedApiKeyLastUsedAt(null);
          setWebhookTagsInput((result.tags ?? []).join(', '));
          setRegenerateOpen(false);
          apiKeyError.clearError();
          notify({
            message: hasConfiguredApiKey
              ? 'Regenerated project API key'
              : 'Generated project API key',
            tone: 'green',
          });
        },
        onError: (err) => {
          apiKeyError.setError(err);
        },
      },
    );
  };
  const onGenerateOrRegenerateWebhookKey = (): void => {
    if (!project) return;
    if (hasConfiguredApiKey) {
      setRegenerateOpen(true);
      return;
    }
    applyGeneratedWebhookKey();
  };
  const onRevokeWebhookKey = (): void => {
    setRevokeOpen(true);
  };
  const onTestWebhookConnection = (): void => {
    if (!(project && hasPlaintextApiKey)) return;
    testProjectPipelineWebhook.mutate(
      { id: project.id, apiKey: apiKeyValue },
      {
        onSuccess: () => {
          setGeneratedApiKeyLastUsedAt(new Date());
          apiKeyError.clearError();
          notify({ message: 'Webhook key is valid and ready to use', tone: 'green' });
        },
        onError: (err) => {
          apiKeyError.setError(err);
        },
      },
    );
  };
  const canTestConnection =
    Boolean(project) &&
    hasPlaintextApiKey &&
    !updateProject.isPending &&
    !rotateProjectApiKey.isPending &&
    !testProjectPipelineWebhook.isPending;
  const content = (
    <Stack gap="md">
      <PipelineWebhookKeySection
        value={displayApiKeyValue}
        canCopy={hasPlaintextApiKey && !updateProject.isPending && !rotateProjectApiKey.isPending}
        canTest={canTestConnection}
        canGenerateOrRegenerate={
          Boolean(project) && !updateProject.isPending && !rotateProjectApiKey.isPending
        }
        canRevoke={
          Boolean(project) &&
          hasConfiguredApiKey &&
          !updateProject.isPending &&
          !rotateProjectApiKey.isPending
        }
        highlightGenerateAction={!hasConfiguredApiKey}
        generateLabel={hasConfiguredApiKey ? 'Regenerate Webhook Key' : 'Generate Webhook Key'}
        actionGroupSx={actionGroupSx}
        actionButtonSx={actionButtonSx}
        onCopy={onCopyWebhookKey}
        onTest={onTestWebhookConnection}
        onGenerateOrRegenerate={onGenerateOrRegenerateWebhookKey}
        onRevoke={onRevokeWebhookKey}
      />
      {hasPlaintextApiKey && !bannerDismissed ? (
        <StatusBanner
          tone="blue"
          variant="outlined"
          message="Copy this key now. It is only shown once."
          onClose={() => {
            setBannerDismissed(true);
          }}
          sx={{
            mt: s('sm'),
            mb: s('sm'),
            '& .MuiAlert-message': {
              py: s('xs'),
            },
          }}
        />
      ) : null}
      {hasConfiguredApiKey ? (
        <PipelineWebhookUrlField
          webhookEndpoint={webhookEndpoint}
          canCopy
          actionGroupSx={actionGroupSx}
          actionButtonSx={actionButtonSx}
          onCopy={onCopyWebhookUrl}
        />
      ) : null}
      {hasConfiguredApiKey ? (
        <Box
          sx={[
            {
              display: 'flex',
              gap: s('md'),
              width: '100%',
              alignItems: 'flex-end',
            },
            settingsWideFieldMeasureSx,
          ]}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack gap="xxs">
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Created
              </Text>
              <Text role="body" sx={settingsValueTextSx}>
                {createdLabel}
              </Text>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack gap="xxs">
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Last Used
              </Text>
              <Text role="body" sx={settingsValueTextSx}>
                {lastUsedLabel}
              </Text>
            </Stack>
          </Box>
          <Button
            variant="secondary"
            fullWidthOnMobile={false}
            disabled
            tabIndex={-1}
            aria-hidden="true"
            sx={{
              visibility: 'hidden',
              pointerEvents: 'none',
            }}
          >
            View Snippet
          </Button>
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <>
      {container === 'panel' ? renderPanel('Pipeline Webhook Key', content) : content}
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate Webhook Key"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Generate a new webhook key for this project.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action revokes the existing webhook key immediately.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Update your pipeline webhook secret token after regenerating to avoid webhook
              failures.
            </Text>
          </Stack>
        }
        confirmLabel="Regenerate Webhook Key"
        confirmColor="primary"
        confirmVariant="contained"
        loading={updateProject.isPending || rotateProjectApiKey.isPending}
        onClose={() => {
          setRegenerateOpen(false);
        }}
        onConfirm={() => {
          applyGeneratedWebhookKey();
        }}
      />
      <ConfirmDialog
        open={revokeOpen}
        title="Revoke Webhook Key"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Delete this webhook key to immediately block pipeline webhook requests using it.
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              This action cannot be undone.
            </Text>
          </Stack>
        }
        confirmLabel="Revoke Webhook Key"
        confirmColor="error"
        loading={updateProject.isPending || rotateProjectApiKey.isPending}
        onClose={() => {
          setRevokeOpen(false);
        }}
        onConfirm={() => {
          if (!project) return;
          updateProject.mutate(
            { id: project.id, projectApiKey: null },
            {
              onSuccess: () => {
                setApiKeyValue('');
                setGeneratedApiKeyCreatedAt(null);
                setGeneratedApiKeyLastUsedAt(null);
                apiKeyError.clearError();
                notify({ message: 'Revoked webhook key', tone: 'green' });
                setRevokeOpen(false);
              },
              onError: (err) => {
                apiKeyError.setError(err);
              },
            },
          );
        }}
      />
    </>
  );
};
